// AttachmentList — renders attachments under a user turn in chat history.
//
// Images: small authed-fetched thumbnail that opens the app's shared
// `ImageLightbox` (`unify-image-lightbox`) on click. Documents: FileText icon +
// filename chip that downloads on click. Non-"ready" rows get a neutral chip
// with no interactive preview.
//
// The LIST owns the gallery. The set is the message's ready image attachments,
// in order (documents and not-ready images are outside it), `activeKey` is the
// attachment id of the one that is open, and their bytes are fetched here, once each, through
// `useAuthedBlobUrls`. Thumbnails and the lightbox read that same map, so an
// image whose thumbnail already loaded is never fetched again to be shown large
// and walking the gallery (previous/next, `ArrowLeft`/`ArrowRight`, wrap, the
// `n de N` indicator — all inside `ImageLightbox`) costs no request. A sibling
// that is still loading, or whose fetch failed, shows that state in place in
// the lightbox rather than closing it or being skipped.
//
// `ImageThumbnail` is purely presentational: it receives the `LightboxImage`
// its blob resolved to (loading / ready / unavailable) and an `onOpen`. A blob
// that failed to load shows the same "!" box as a failed attachment instead of
// spinning forever.
//
// `compact` halves the image box (64px -> 32px). The user card that hosts this
// list is `position: sticky`, so while it is collapsed its full height is
// charged against the viewport on every scroll; the caller asks for compact
// there and for full size once the card is deliberately expanded. Every branch
// (ready / loading / not-ready) reserves the same box so the card does not
// reflow when a blob lands.
//
// Object URLs created by `useAuthedBlobUrls` are revoked when an image leaves
// the list and on unmount, so nothing leaks.

import {
  ImageLightbox,
  type LightboxImage,
  type LightboxSlide,
} from "@/components/ui/image-lightbox";
import { type ChatMessageAttachment, fetchAttachmentBlob } from "@/lib/chat";
import { lightboxImageOf, useAuthedBlobUrls } from "@/lib/use-authed-blob-urls";
import { cn } from "@/lib/utils";
import { FileText, Loader } from "lucide-react";
import { type JSX, useState } from "react";

// ---------------------------------------------------------------------------
// ImageThumbnail — presentational: a small thumbnail that asks to be opened
// ---------------------------------------------------------------------------

function ImageThumbnail({
  attachment,
  compact,
  image,
  onOpen,
}: {
  attachment: ChatMessageAttachment;
  compact: boolean;
  /** What the fetched blob is right now: loading, ready (object URL) or failed. */
  image: LightboxImage;
  onOpen: () => void;
}): JSX.Element {
  const isReady = attachment.status === "ready";

  // Same box in every branch — the placeholder must not resize when the blob
  // resolves, or the sticky card jumps under the reader.
  const boxClass = compact ? "h-8 w-8" : "h-16 w-16";
  const spinnerSize = compact ? 11 : 14;
  const testId = `attachment-thumbnail-${attachment.id}`;

  // Not ready, or ready but its blob failed to load: a neutral, non-interactive
  // box. Only a pending attachment spins; every other dead end shows "!".
  if (!isReady || image.status === "unavailable") {
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-md border border-border bg-muted text-label-2 text-muted-foreground",
          boxClass,
        )}
        title={attachment.filename}
        data-testid={testId}
      >
        {attachment.status === "pending" ? (
          <Loader size={spinnerSize} className="animate-spin" />
        ) : (
          "!"
        )}
      </div>
    );
  }

  if (image.status === "loading") {
    return (
      <div
        className={cn(
          "flex animate-pulse items-center justify-center rounded-md border border-border bg-muted",
          boxClass,
        )}
        title={attachment.filename}
        data-testid={testId}
      >
        <Loader size={spinnerSize} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "overflow-hidden rounded-md border border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        boxClass,
      )}
      aria-label={`Ver imagen: ${attachment.filename}`}
      title={attachment.filename}
      data-testid={testId}
    >
      <img
        src={image.url}
        alt={attachment.filename}
        className="h-full w-full object-cover transition-opacity hover:opacity-80"
      />
    </button>
  );
}

// ---------------------------------------------------------------------------
// DocumentChip — fetches blob and triggers a browser download on click
// ---------------------------------------------------------------------------

function DocumentChip({
  attachment,
}: {
  attachment: ChatMessageAttachment;
}): JSX.Element {
  const [downloading, setDownloading] = useState(false);
  const isReady = attachment.status === "ready";

  async function handleDownload(): Promise<void> {
    if (!isReady || downloading) return;
    setDownloading(true);
    try {
      const blob = await fetchAttachmentBlob(attachment.id);
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = attachment.filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      // Give the browser a moment to start the download before revoking.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } finally {
      setDownloading(false);
    }
  }

  const label = `${isReady ? "Descargar" : "Adjunto"}: ${attachment.filename}`;

  return (
    <button
      type="button"
      onClick={() => void handleDownload()}
      disabled={!isReady || downloading}
      aria-label={label}
      title={label}
      data-testid={`attachment-doc-${attachment.id}`}
      className="flex max-w-[9rem] items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1 text-body-3 text-foreground transition-colors hover:bg-muted disabled:opacity-50"
    >
      {downloading ? (
        <Loader size={14} className="shrink-0 animate-spin text-muted-foreground" />
      ) : (
        <FileText size={14} className="shrink-0 text-muted-foreground" aria-hidden />
      )}
      <span className="min-w-0 flex-1 truncate">{attachment.filename}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// AttachmentList — public component consumed by the chat route
// ---------------------------------------------------------------------------

interface AttachmentListProps {
  attachments: ChatMessageAttachment[];
  /** Halve the image box. Document chips are already one line tall, so they
   *  render the same either way. */
  compact?: boolean;
}

export function AttachmentList({
  attachments,
  compact = false,
}: AttachmentListProps): JSX.Element | null {
  const [activeKey, setActiveKey] = useState<string | null>(null);

  // The gallery: this message's ready images, in order. Only these are fetched.
  const gallery = attachments.filter((att) => att.kind === "image" && att.status === "ready");
  const blobUrls = useAuthedBlobUrls(
    gallery.map((att) => att.id),
    fetchAttachmentBlob,
  );

  if (attachments.length === 0) return null;

  const slides: LightboxSlide[] = gallery.map((att) => ({
    key: att.id,
    alt: att.filename,
    heading: att.filename,
    image: lightboxImageOf(blobUrls.get(att.id)),
  }));

  return (
    <div
      className={cn("flex flex-wrap items-start", compact ? "gap-1.5" : "gap-2")}
      data-testid="attachment-list"
      data-compact={compact ? "true" : "false"}
    >
      {attachments.map((att) =>
        att.kind === "image" ? (
          <ImageThumbnail
            key={att.id}
            attachment={att}
            compact={compact}
            image={lightboxImageOf(blobUrls.get(att.id))}
            onOpen={() => setActiveKey(att.id)}
          />
        ) : (
          <DocumentChip key={att.id} attachment={att} />
        ),
      )}
      <ImageLightbox
        slides={slides}
        activeKey={activeKey}
        onActiveKeyChange={setActiveKey}
        onClose={() => setActiveKey(null)}
      />
    </div>
  );
}
